/**
 * Usage billing report (Zac, 2026-10-28): what each practice's texting
 * (Twilio SMS/MMS and numbers) and OpenAI calls cost Computers2Go, and the
 * client price at a multiplier of that cost (default 2x, the same per-practice
 * multiplier as AI Usage). Reporting only: nothing here charges, invoices or
 * creates a Square subscription. Charging needs the per-practice switch,
 * which defaults to off, and even then no invoice run reads usage yet.
 *
 * Money is integer micro-dollars (1 USD = 1,000,000 micros).
 *
 * Cost sources, in order:
 *   - SMS/MMS: Twilio's own price when the message row carries it
 *     (`interaction.sms.price`), else the configured rate below.
 *   - Twilio numbers: the configured monthly rate per number on a Velora
 *     Twilio line of the office.
 *   - Caller ID lookups (Twilio Lookup caller_name, phones_caller_id_lookup):
 *     the cost each lookup recorded ($0.01 list); counted with texting
 *     (Twilio) as `sms.callerIdLookups`.
 *   - OpenAI: the cost each call locked in when recorded (ai_usage_call),
 *     billed at the multiplier it locked in.
 *   - On-prem/local models (GLM, Qwen, rules): $0 to Computers2Go, and not
 *     recorded at all; no internal rate is configured.
 *
 * Who sees what: C2G Admin sees cost, client price and margin. The practice
 * sees the client price only, never the cost or the multiplier.
 */
import { z } from 'zod';

const Micros = z.number().int();

/** Twilio US list prices used when a message carries no price of its own. */
export const DEFAULT_SMS_RATES = Object.freeze({
  /** Per outbound SMS segment ($0.0083). Carrier fees are not included. */
  smsOutSegmentMicros: 8_300,
  /** Per inbound SMS segment ($0.0083). */
  smsInSegmentMicros: 8_300,
  /** Per outbound MMS ($0.0220). */
  mmsOutMicros: 22_000,
  /** Per inbound MMS ($0.0220). */
  mmsInMicros: 22_000,
  /** Per local number per month ($1.15). */
  numberMonthMicros: 1_150_000,
});
export type SmsRates = { -readonly [K in keyof typeof DEFAULT_SMS_RATES]: number };

/** Date of the Twilio list prices above. */
export const SMS_RATES_AS_OF = '2026-10-28';

/** Client price = cost x 2 unless C2G changes the practice's multiplier. */
export const DEFAULT_USAGE_MULTIPLIER_BP = 20_000;

/** Months of usage history shown (this month included). */
export const USAGE_HISTORY_MONTHS = 12;

export const USAGE_AI_LANES = [
  'ask',
  'scribe',
  'txplan',
  'summarize',
  'receptionist',
  'call_analysis',
  'other',
] as const;
export type UsageAiLane = (typeof USAGE_AI_LANES)[number];
export const USAGE_AI_LANE_LABELS: Readonly<Record<UsageAiLane, string>> = {
  ask: 'Ask Velora',
  scribe: 'Scribe / clinical notes',
  txplan: 'Treatment plans',
  summarize: 'Summaries',
  receptionist: 'Receptionist',
  call_analysis: 'Call analysis',
  other: 'Other AI',
};

/** Gateway purpose -> report lane. */
export function usageAiLaneOf(purpose: string | null): UsageAiLane {
  const p = purpose ?? '';
  if (p.startsWith('ask.')) return 'ask';
  if (p.startsWith('scribe.') || p.startsWith('notes.')) return 'scribe';
  if (p.startsWith('txplan.')) return 'txplan';
  if (p.endsWith('.summarize') || p.startsWith('summarize')) return 'summarize';
  if (p.startsWith('receptionist.') || p.startsWith('recep.')) return 'receptionist';
  if (p.startsWith('call.') || p.startsWith('calls.') || p.startsWith('phones.'))
    return 'call_analysis';
  return 'other';
}

export const UsageCostPriceSchema = z.object({
  costMicros: Micros,
  priceMicros: Micros,
});

export const UsageMonthSchema = z.object({
  /** YYYY-MM in the practice's time zone. */
  month: z.string(),
  sms: z.object({
    outMessages: z.number().int(),
    outSegments: z.number().int(),
    inMessages: z.number().int(),
    inSegments: z.number().int(),
    mmsOut: z.number().int(),
    mmsIn: z.number().int(),
    /** Messages priced at Twilio's own price; the rest at the configured rate. */
    twilioPriced: z.number().int(),
    numbers: z.number().int(),
    /** Caller ID (CNAM) lookups for unmatched callers; their cost is in costMicros. */
    callerIdLookups: z.number().int().default(0),
    costMicros: Micros,
    priceMicros: Micros,
  }),
  ai: z.object({
    calls: z.number().int(),
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    /** Calls on a model with no price (not in cost or price). */
    unpricedCalls: z.number().int(),
    costMicros: Micros,
    priceMicros: Micros,
    lanes: z.array(
      z.object({
        lane: z.enum(USAGE_AI_LANES),
        label: z.string(),
        calls: z.number().int(),
        inputTokens: z.number().int(),
        outputTokens: z.number().int(),
        costMicros: Micros,
        priceMicros: Micros,
      }),
    ),
  }),
  offices: z.array(
    z.object({
      locationId: z.uuid().nullable(),
      name: z.string(),
      costMicros: Micros,
      priceMicros: Micros,
    }),
  ),
  costMicros: Micros,
  priceMicros: Micros,
  marginMicros: Micros,
});
export type UsageMonth = z.infer<typeof UsageMonthSchema>;

/** C2G Admin: a practice's usage with cost, client price and margin. */
export const SupportUsageSchema = z.object({
  /** Client price multiplier in ten-thousandths (20,000 = 2x). */
  multiplierBp: z.number().int(),
  /** The practice is charged for usage. Default false ("would be billed"). */
  billingEnabled: z.boolean(),
  rates: z.object({
    smsOutSegmentMicros: Micros,
    smsInSegmentMicros: Micros,
    mmsOutMicros: Micros,
    mmsInMicros: Micros,
    numberMonthMicros: Micros,
    asOf: z.string(),
  }),
  /** Oldest first, this month last. */
  months: z.array(UsageMonthSchema),
});
export type SupportUsage = z.infer<typeof SupportUsageSchema>;

export const SupportUsageBillingRequestSchema = z.object({ enabled: z.boolean() });
export type SupportUsageBillingRequest = z.infer<typeof SupportUsageBillingRequestSchema>;

/** The practice's own view: client price only. */
export const PracticeUsageMonthSchema = z.object({
  month: z.string(),
  smsMessages: z.number().int(),
  smsSegments: z.number().int(),
  numbers: z.number().int(),
  /** Caller ID (CNAM) lookups; their price is in smsPriceMicros. */
  callerIdLookups: z.number().int().default(0),
  smsPriceMicros: Micros,
  aiCalls: z.number().int(),
  aiPriceMicros: Micros,
  priceMicros: Micros,
  offices: z.array(
    z.object({ locationId: z.uuid().nullable(), name: z.string(), priceMicros: Micros }),
  ),
});
export type PracticeUsageMonth = z.infer<typeof PracticeUsageMonthSchema>;

export const PracticeUsageSchema = z.object({
  /** False: the amounts are what usage would be billed at; nothing is charged. */
  billingEnabled: z.boolean(),
  timeZone: z.string(),
  months: z.array(PracticeUsageMonthSchema),
});
export type PracticeUsage = z.infer<typeof PracticeUsageSchema>;
